import type { Provider } from "@src/interfaces/provider.interface";

export interface ProviderPartition {
  clinicProviders: Provider[];
  otherProviders: Provider[];
  isClinicMode: boolean;
}

/**
 * Partitions a provider list into clinic providers and other providers based on
 * the active theme's providerGroupId.
 *
 * Accepts a single ID or an array of IDs to support clinics that span multiple
 * provider groups.
 *
 * When providerGroupId is undefined, all providers are returned as clinicProviders
 * with isClinicMode: false (no partitioning applied).
 *
 * When providerGroupId is set but no providers match (zero-clinic fallback), all
 * providers are returned as clinicProviders with isClinicMode: false so the UI
 * does not render an empty clinic section.
 */
export function partitionProvidersByGroup(
  providers: Provider[],
  providerGroupId: number | number[] | undefined,
): ProviderPartition {
  if (providerGroupId === undefined) {
    return { clinicProviders: providers, otherProviders: [], isClinicMode: false };
  }

  const groupIds = Array.isArray(providerGroupId) ? providerGroupId : [providerGroupId];

  const clinicProviders = providers.filter((p) => groupIds.includes(p.providerGroupId));
  const otherProviders = providers.filter((p) => !groupIds.includes(p.providerGroupId));

  // Zero-clinic fallback: if the group IDs are set but nothing matches, treat as
  // normal (unpartitioned) so the user sees all available providers.
  if (clinicProviders.length === 0) {
    return { clinicProviders: providers, otherProviders: [], isClinicMode: false };
  }

  return { clinicProviders, otherProviders, isClinicMode: true };
}

/**
 * Partitions providers by the active theme's local-center group and department scope.
 * Themes without department scope retain the existing group-only partition behavior.
 */
export function partitionProvidersByGroupAndDepartment(
  providers: Provider[],
  providerGroupId: number | number[] | undefined,
  departmentIds: number[] | undefined,
): ProviderPartition {
  if (!departmentIds || departmentIds.length === 0) {
    return partitionProvidersByGroup(providers, providerGroupId);
  }

  if (providerGroupId === undefined) {
    return { clinicProviders: providers, otherProviders: [], isClinicMode: false };
  }

  const groupIds = new Set(Array.isArray(providerGroupId) ? providerGroupId : [providerGroupId]);
  const configuredDepartmentIds = new Set(departmentIds);
  const isClinicProvider = (provider: Provider): boolean =>
    groupIds.has(provider.providerGroupId) &&
    provider.departments.some((department) =>
      configuredDepartmentIds.has(department.department_id),
    );
  const clinicProviders = providers.filter(isClinicProvider);
  const otherProviders = providers.filter((provider) => !isClinicProvider(provider));

  if (clinicProviders.length === 0) {
    return { clinicProviders: providers, otherProviders: [], isClinicMode: false };
  }

  return { clinicProviders, otherProviders, isClinicMode: true };
}

/**
 * Resolves the clinic label from the configured department scope for the
 * combined group-and-department priority behavior.
 */
export function getClinicNameForDepartmentScope(
  providers: Provider[],
  departmentIds: number[] | undefined,
): string | undefined {
  if (!departmentIds || departmentIds.length === 0) {
    return undefined;
  }

  const configuredDepartmentIds = new Set(departmentIds);
  for (const provider of providers) {
    const department = provider.departments.find((item) =>
      configuredDepartmentIds.has(item.department_id),
    );
    const clinicName = department?.clinic_name.trim();
    if (clinicName) {
      return clinicName;
    }
  }

  return undefined;
}
